import type { StreamFormat } from '@auxcast/protocol';
import { parseFormat } from '@auxcast/protocol';

/** Shape of `GET /api/sessions/:id` — docs/PROTOCOL.md §2. */
export interface SessionInfo {
  id: string;
  name: string;
  live: boolean;
  requiresPasscode: boolean;
  listeners: number;
  startedAt: number | null;
  format: StreamFormat | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly kind: 'not-found' | 'offline' | 'bad-response' | 'server',
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Normalise the relay's session document. The relay is trusted, but a stale
 * deployment or a reverse proxy serving an error page as JSON should degrade into
 * a usable listener page rather than a blank screen, so every field has a fallback.
 */
export function normalizeSessionInfo(raw: unknown, fallbackId: string): SessionInfo {
  if (!isRecord(raw)) {
    throw new ApiError('session metadata was not a JSON object', 200, 'bad-response');
  }
  const formatResult = parseFormat(raw['format']);
  return {
    id: typeof raw['id'] === 'string' ? raw['id'] : fallbackId,
    name: typeof raw['name'] === 'string' && raw['name'] !== '' ? raw['name'] : 'Untitled session',
    live: raw['live'] === true,
    requiresPasscode: raw['requiresPasscode'] === true,
    listeners: Math.max(0, Math.trunc(asFiniteNumber(raw['listeners'], 0))),
    startedAt: typeof raw['startedAt'] === 'number' ? raw['startedAt'] : null,
    format: formatResult.ok ? formatResult.value : null,
  };
}

export async function fetchSession(id: string, signal?: AbortSignal): Promise<SessionInfo> {
  let response: Response;
  try {
    response = await fetch(`/api/sessions/${encodeURIComponent(id)}`, {
      signal,
      headers: { accept: 'application/json' },
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError('could not reach the relay', 0, 'offline');
  }

  if (response.status === 404) {
    throw new ApiError('no session with that code', 404, 'not-found');
  }
  if (!response.ok) {
    throw new ApiError(`relay returned ${response.status}`, response.status, 'server');
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError('relay returned a malformed session document', response.status, 'bad-response');
  }
  return normalizeSessionInfo(body, id);
}
